/**
 * Ricarica la pagina quando un chunk lazy non esiste piu' sul server.
 *
 * IL PROBLEMA, visto dal vivo su preprod il 18/08.
 * L'app e' divisa in chunk col nome hashato (`3148.ea1c46e5.chunk.js`). Chi ha
 * la scheda aperta durante un rilascio tiene in memoria l'`index.html` vecchio,
 * che punta a quei nomi. Dopo il deploy quei file non ci sono piu': il server
 * non li trova, il rewrite SPA risponde con `index.html`, e il browser che si
 * aspettava JavaScript trova `<` e lancia `Unexpected token '<'`.
 * Per l'utente non e' un errore in console: e' una schermata che non si apre e
 * basta. E' successo aprendo la scheda invito dello switch di ruolo.
 *
 * PERCHE' UN RELOAD E NON UN RETRY.
 * Ritentare lo stesso URL non serve a niente: quel file non tornera' mai:
 * l'unico modo di ottenere i nomi nuovi e' rileggere l'`index.html`.
 *
 * PERCHE' IL CONTATORE.
 * Se il deploy e' davvero rotto (chunk mancante anche nell'indice nuovo), un
 * reload incondizionato manda l'utente in un ciclo infinito di ricariche, che
 * e' peggio del difetto che cura. Dopo MAX_TENTATIVI si smette e si lascia
 * passare l'errore a chi lo sa registrare.
 *
 * LA TRACCIA (STU-424).
 * Una ricarica butta via tutto quello che vive solo in memoria. Il 23/09, su
 * un browser, l'app e' ripartita 10 volte in 48 minuti senza nessun rilascio,
 * e nessun evento diceva perche'. Ogni ricarica decisa qui lascia una traccia
 * nella sessionStorage (motivo, file, pagina, tentativo); la pagina che nasce
 * dalla ricarica la manda come evento `stale_chunk_reload` e la cancella
 * prima, cosi' ogni ricarica si racconta al massimo una volta. Mandarla prima
 * di ricaricare vorrebbe dire affidarla a una pagina che sta morendo.
 */
import { trackEvent } from './analytics';

export const CHIAVE = 'studierAI_stale_chunk_reload';
export const CHIAVE_TRACCIA = 'studierAI_stale_chunk_reload_traccia';
export const EVENTO_RICARICA = 'stale_chunk_reload';
export const MAX_TENTATIVI = 2;
/** Oltre questa distanza i tentativi precedenti riguardano un altro episodio. */
export const FINESTRA_MS = 60 * 1000;
/** Le tracce che aspettano l'avvio: se nessuna pagina riesce a mandarle, non crescono all'infinito. */
export const MAX_TRACCE = 10;
/** GA4 taglia i valori dei parametri a 100 caratteri. */
const LUNGHEZZA_MAX = 100;

const MOTIVI = [
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

/**
 * Un `SyntaxError` con `<` che arriva da un file .js e' HTML servito al posto
 * del codice: il caso del rewrite SPA. Il controllo sulla provenienza e'
 * necessario, altrimenti qualunque JSON malformato dell'app farebbe ricaricare.
 */
const isHtmlAlPostoDiJs = (messaggio, origine) => {
  if (!/unexpected token '?</i.test(messaggio)) return false;
  return typeof origine === 'string' && /\.js(\?|$)/i.test(origine);
};

export const eUnChunkSparito = (messaggio, origine) => {
  const m = String(messaggio || '');
  if (MOTIVI.some((re) => re.test(m))) return true;
  return isHtmlAlPostoDiJs(m, origine);
};

/** Legge lo stato dei tentativi; una sessionStorage inaccessibile non deve rompere nulla. */
const leggiStato = (storage, adesso) => {
  try {
    const grezzo = storage.getItem(CHIAVE);
    if (!grezzo) return { tentativi: 0 };
    const stato = JSON.parse(grezzo);
    if (!stato || typeof stato.tentativi !== 'number') return { tentativi: 0 };
    if (adesso - (stato.quando || 0) > FINESTRA_MS) return { tentativi: 0 };
    return stato;
  } catch {
    return { tentativi: 0 };
  }
};

const scriviStato = (storage, stato) => {
  try {
    storage.setItem(CHIAVE, JSON.stringify(stato));
  } catch {
    /* modalita' privata o quota piena: si perde il conteggio, non l'app */
  }
};

const testo = (valore) => (typeof valore === 'string' && valore ? valore.slice(0, LUNGHEZZA_MAX) : undefined);

/** Solo il nome del file: l'indirizzo intero non entra nei 100 caratteri di GA4. */
const nomeFile = (origine) => {
  if (typeof origine !== 'string' || !origine) return undefined;
  const senzaQuery = origine.split(/[?#]/)[0];
  return testo(senzaQuery.slice(senzaQuery.lastIndexOf('/') + 1) || senzaQuery);
};

/** La ricarica che sta per partire, per la pagina che nascera'. */
const lasciaTraccia = (storage, traccia) => {
  try {
    const grezzo = storage.getItem(CHIAVE_TRACCIA);
    const prima = grezzo ? JSON.parse(grezzo) : [];
    const tracce = Array.isArray(prima) ? prima : [];
    tracce.push(traccia);
    storage.setItem(CHIAVE_TRACCIA, JSON.stringify(tracce.slice(-MAX_TRACCE)));
  } catch {
    /* si perde la traccia, non la ricarica */
  }
};

/**
 * @param {string} [sorgente] chi ha visto l'errore: 'window_error',
 *   'window_rejection' o 'route_error'; finisce nella traccia.
 * @param {string} [pagina] il percorso su cui l'errore e' arrivato.
 * @returns {boolean} true se ha ricaricato (chi chiama deve fermare l'errore)
 */
export const gestisciChunkSparito = ({ messaggio, origine, storage, reload, adesso, sorgente, pagina }) => {
  if (!eUnChunkSparito(messaggio, origine)) return false;
  const stato = leggiStato(storage, adesso);
  if (stato.tentativi >= MAX_TENTATIVI) return false;
  scriviStato(storage, { tentativi: stato.tentativi + 1, quando: adesso });
  lasciaTraccia(storage, {
    sorgente: testo(sorgente),
    messaggio: testo(String(messaggio || '')),
    file: nomeFile(origine),
    pagina: testo(pagina),
    tentativo: stato.tentativi + 1,
    quando: adesso,
  });
  reload();
  return true;
};

const senzaVuoti = (parametri) =>
  Object.fromEntries(Object.entries(parametri).filter(([, valore]) => valore !== undefined));

/**
 * All'avvio: manda un evento per ogni ricarica lasciata in traccia dalle
 * pagine di prima, e prima la cancella. Se la cancellazione non riesce non
 * manda niente: meglio un evento perso che lo stesso contato a ogni avvio.
 * @returns {number} quante ricariche ha raccontato
 */
export const raccontaRicariche = ({ storage, traccia = trackEvent, adesso = Date.now() } = {}) => {
  if (!storage) return 0;
  let tracce;
  try {
    const grezzo = storage.getItem(CHIAVE_TRACCIA);
    if (!grezzo) return 0;
    storage.removeItem(CHIAVE_TRACCIA);
    tracce = JSON.parse(grezzo);
  } catch {
    return 0;
  }
  if (!Array.isArray(tracce)) return 0;
  let raccontate = 0;
  tracce.forEach((voce) => {
    if (!voce || typeof voce !== 'object') return;
    try {
      traccia(EVENTO_RICARICA, senzaVuoti({
        category: 'error',
        source: testo(voce.sorgente),
        reason: testo(voce.messaggio),
        chunk_file: testo(voce.file),
        from_path: testo(voce.pagina),
        attempt: Number.isFinite(voce.tentativo) ? voce.tentativo : undefined,
        ms_since_reload: Number.isFinite(voce.quando) ? Math.max(0, adesso - voce.quando) : undefined,
      }));
      raccontate += 1;
    } catch {
      /* l'analytics non deve mai fermare l'avvio */
    }
  });
  return raccontate;
};

/**
 * Da chiamare una volta all'avvio: racconta le ricariche delle pagine di prima
 * e si mette in ascolto. Restituisce la funzione per disinstallare.
 */
export const installStaleChunkReload = ({
  finestra = typeof window !== 'undefined' ? window : undefined,
  storage = finestra && finestra.sessionStorage,
  reload = () => finestra.location.reload(),
  orologio = () => Date.now(),
  traccia = trackEvent,
} = {}) => {
  if (!finestra || !storage) return () => {};

  raccontaRicariche({ storage, traccia, adesso: orologio() });

  const paginaAttuale = () => (finestra.location && finestra.location.pathname) || undefined;

  const suErrore = (event) => {
    const ricaricato = gestisciChunkSparito({
      messaggio: event.message || (event.error && event.error.message),
      origine: event.filename,
      storage,
      reload,
      adesso: orologio(),
      sorgente: 'window_error',
      pagina: paginaAttuale(),
    });
    if (ricaricato) {
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    }
  };

  const suRifiuto = (event) => {
    const motivo = event.reason;
    const ricaricato = gestisciChunkSparito({
      messaggio: (motivo && motivo.message) || String(motivo || ''),
      origine: motivo && motivo.fileName,
      storage,
      reload,
      adesso: orologio(),
      sorgente: 'window_rejection',
      pagina: paginaAttuale(),
    });
    if (ricaricato) {
      event.preventDefault();
      if (event.stopImmediatePropagation) event.stopImmediatePropagation();
    }
  };

  // capture: davanti al gestore globale di index.js, che altrimenti si limita
  // a registrare l'errore e lascia l'utente sulla schermata che non si apre.
  finestra.addEventListener('error', suErrore, true);
  finestra.addEventListener('unhandledrejection', suRifiuto, true);

  return () => {
    finestra.removeEventListener('error', suErrore, true);
    finestra.removeEventListener('unhandledrejection', suRifiuto, true);
  };
};
